import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Play, Headphones, Wrench } from "lucide-react";
import api from "@/lib/api";
import { InterviewCard } from "@/components/site/Cards";

/* ------------------------------------------------------------------ */
/* HERO — plain statement of what Operating Class is                   */
/* ------------------------------------------------------------------ */
export function HeroSection() {
  return (
    <section className="border-b border-oc-hairline">
      <div className="max-w-editorial mx-auto px-6 md:px-10 pt-16 md:pt-28 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <div className="eyebrow text-oc-coral mb-8 flex items-center">
              <span className="oc-square" /> Business media &amp; practical business education
            </div>
            <h1 className="hero-headline text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-oc-ink">
              Business lessons from the people
              <span className="text-oc-blue"> actually operating businesses</span>.
            </h1>
            <p className="mt-8 font-editorial text-xl md:text-2xl text-oc-ink-soft leading-snug max-w-2xl">
              Operating Class publishes practical articles, interviews, and frameworks about the real problems involved in building and running organizations. Written for founders, owners, executives, managers, and the operators responsible for making things work.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/read" data-testid="hero-read-btn" className="inline-flex items-center justify-center h-14 px-8 bg-oc-ink text-white font-ui text-sm font-bold uppercase tracking-widest hover:bg-oc-blue transition-colors">
                Start reading <ArrowRight className="w-4 h-4 ml-3" />
              </Link>
              <Link to="/contribute" data-testid="hero-contribute-btn" className="inline-flex items-center justify-center h-14 px-8 bg-oc-coral text-white font-ui text-sm font-bold uppercase tracking-widest hover:bg-oc-coral-hover transition-colors">
                Contribute your experience
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="border-l-2 border-oc-coral pl-6 py-2">
              <div className="eyebrow text-oc-ink-muted mb-3">Edited by</div>
              <p className="font-editorial text-base md:text-lg text-oc-ink-soft leading-relaxed">
                <a
                  href="https://quentinyoung.ca"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="home-quentin-link"
                  className="text-oc-ink font-semibold underline underline-offset-4 decoration-oc-coral/50 hover:decoration-oc-coral"
                >
                  Quentin Young
                </a>. An independent publication assembling a working library of operator knowledge, one conversation at a time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* PROBLEM PICKER — "What are you trying to solve?"                    */
/* ------------------------------------------------------------------ */
const PROBLEMS = [
  { title: "Hiring the right people", topic: "hiring" },
  { title: "Delegating without losing control", topic: "delegation" },
  { title: "Pricing our work", topic: "pricing" },
  { title: "Fixing cash flow", topic: "cash-flow" },
  { title: "Building better systems", topic: "systems" },
  { title: "Improving leadership", topic: "leadership" },
  { title: "Setting real standards", topic: "standards" },
  { title: "Growing without chaos", topic: "growth" },
  { title: "Building a healthier culture", topic: "culture" },
  { title: "Reading the numbers", topic: "reading-numbers" },
];

export function ProblemPickerSection() {
  return (
    <section className="bg-oc-sand border-b border-oc-hairline" data-testid="home-problem-picker">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-3xl">
          <div className="eyebrow text-oc-coral mb-6">Enter through the problem</div>
          <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-oc-ink">
            What are you trying to solve?
          </h2>
          <p className="mt-6 font-editorial text-lg md:text-xl text-oc-ink-soft leading-snug max-w-2xl">
            Pick a real problem and go straight to the interviews, contributors, and frameworks that address it.
          </p>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {PROBLEMS.map((p, i) => (
            <Link
              key={p.topic}
              to={`/topics/${p.topic}`}
              data-testid={`problem-card-${p.topic}`}
              className="group flex items-start gap-3 p-5 bg-white border border-oc-hairline hover:border-oc-ink hover:bg-oc-ink hover:text-white transition-colors"
            >
              <span className="font-ui text-xs font-bold text-oc-coral group-hover:text-oc-coral pt-0.5 w-6 flex-shrink-0">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-editorial text-base md:text-lg leading-snug">
                {p.title}
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-10">
          <Link to="/topics" className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-blue text-oc-blue pb-1 hover:text-oc-coral hover:border-oc-coral">
            Browse all problems <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* WHAT IS SECTION — plain-language category definition                */
/* ------------------------------------------------------------------ */
export function WhatIsSection() {
  return (
    <section className="border-b border-oc-hairline">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
          <div>
            <div className="eyebrow text-oc-coral mb-6">What Operating Class is</div>
            <p className="font-editorial text-2xl md:text-3xl leading-snug text-oc-ink">
              A business media and practical education platform. We publish articles, interviews, conversations, and frameworks about the operational problems that founders, owners, executives, and managers actually deal with.
            </p>
            <Link to="/about" className="mt-8 inline-flex font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-blue text-oc-blue pb-1 hover:text-oc-coral hover:border-oc-coral">
              How the publication works <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Link>
          </div>
          <div>
            <div className="eyebrow text-oc-coral mb-6">Who it is for</div>
            <p className="font-editorial text-2xl md:text-3xl leading-snug text-oc-ink">
              Founders, owners, executives, managers, operators, and ambitious professionals looking for useful lessons from people with real operating experience.
            </p>
            <p className="mt-6 font-editorial text-lg text-oc-ink-soft leading-relaxed">
              The standard isn&#39;t fame. The standard is: have you actually done something worth teaching?
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* FEATURED INTERVIEW                                                  */
/* ------------------------------------------------------------------ */
export function FeaturedInterviewSection({ interview }) {
  if (!interview) return null;
  return (
    <section className="border-b border-oc-hairline">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="mb-12 flex items-baseline justify-between">
          <div className="eyebrow text-oc-ink-muted">This Week&#39;s Featured Conversation</div>
          <Link to="/read" className="hidden md:inline-flex font-ui text-xs font-bold uppercase tracking-widest text-oc-ink hover:text-oc-blue">All articles &rarr;</Link>
        </div>
        <InterviewCard interview={interview} variant="feature" testId="home-featured-interview" />
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* FORMATS SECTION — Read / Watch / Listen / Use                       */
/* ------------------------------------------------------------------ */
const FORMATS = [
  {
    to: "/read",
    icon: BookOpen,
    label: "Read",
    description: "Articles, interviews, frameworks, and case studies.",
    status: "Live",
  },
  {
    to: "/watch",
    icon: Play,
    label: "Watch",
    description: "Interviews, short video, discussions, explainers.",
    status: "Coming soon",
  },
  {
    to: "/listen",
    icon: Headphones,
    label: "Listen",
    description: "Audio conversations and podcasts.",
    status: "Coming soon",
  },
  {
    to: "/use",
    icon: Wrench,
    label: "Use",
    description: "Templates, tools, checklists, frameworks.",
    status: "Coming soon",
  },
];

export function FormatsSection() {
  return (
    <section className="bg-oc-beige border-b border-oc-hairline" data-testid="home-formats">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow text-oc-coral mb-6">Formats</div>
          <h2 className="font-editorial text-4xl md:text-5xl leading-[1.05] tracking-tight text-oc-ink">
            One publication. Four ways in.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FORMATS.map((f) => {
            const Icon = f.icon;
            const isLive = f.status === "Live";
            return (
              <Link
                key={f.label}
                to={f.to}
                data-testid={`format-card-${f.label.toLowerCase()}`}
                className="group relative flex flex-col p-8 bg-white border border-oc-hairline hover:border-oc-ink transition-colors"
              >
                <Icon className="w-6 h-6 text-oc-blue mb-6" />
                <div className="font-editorial text-3xl text-oc-ink group-hover:text-oc-blue transition-colors">
                  {f.label}
                </div>
                <p className="mt-3 font-editorial text-base text-oc-ink-soft leading-relaxed">
                  {f.description}
                </p>
                <div className="mt-6 pt-6 border-t border-oc-hairline">
                  <span
                    className={`font-ui text-xs font-bold uppercase tracking-widest ${
                      isLive ? "text-oc-blue" : "text-oc-ink-muted"
                    }`}
                  >
                    {f.status}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* FOR WHOM — Readers / Contributors / Partners                        */
/* ------------------------------------------------------------------ */
export function ForWhomSection() {
  return (
    <section className="border-b border-oc-hairline bg-white" data-testid="home-for-whom">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="max-w-2xl mb-14">
          <div className="eyebrow text-oc-coral mb-6">Who this is for</div>
          <h2 className="font-editorial text-4xl md:text-5xl leading-[1.05] tracking-tight text-oc-ink">
            Three ways to be part of Operating Class today.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ForWhomCard
            label="For readers"
            headline="Subscribe, read, and follow along."
            body="Get useful lessons from operators with real experience. No hustle content, no manufactured success stories."
            cta="Subscribe to the newsletter"
            to="/newsletter"
            testId="forwhom-readers"
          />
          <ForWhomCard
            label="For contributors"
            headline="Pitch an idea or get interviewed."
            body="Turn your operating experience into something useful to other operators. Editorial support included."
            cta="Contribute your experience"
            to="/contribute"
            testId="forwhom-contributors"
            accent="coral"
          />
          <ForWhomCard
            label="For partners"
            headline="Sponsor, advertise, or collaborate."
            body="Reach an audience of founders, owners, and operators through clearly labeled sponsorship and partnerships."
            cta="See partnership options"
            to="/partner"
            testId="forwhom-partners"
          />
        </div>
      </div>
    </section>
  );
}

function ForWhomCard({ label, headline, body, cta, to, testId, accent }) {
  const borderColor = accent === "coral" ? "border-oc-coral" : "border-oc-hairline hover:border-oc-ink";
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group flex flex-col p-8 md:p-10 bg-white border-2 ${borderColor} transition-colors`}
    >
      <div className="eyebrow text-oc-coral mb-4">{label}</div>
      <h3 className="font-editorial text-2xl md:text-3xl leading-tight text-oc-ink group-hover:text-oc-blue transition-colors">
        {headline}
      </h3>
      <p className="mt-4 font-editorial text-base text-oc-ink-soft leading-relaxed flex-1">
        {body}
      </p>
      <div className="mt-8 pt-6 border-t border-oc-hairline">
        <span className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest text-oc-blue group-hover:text-oc-coral">
          {cta} <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* CONTRIBUTOR SNIPPETS                                                */
/* ------------------------------------------------------------------ */
export function ContributorSnippetsSection({ operators = [] }) {
  if (!operators.length) return null;
  const picks = operators.slice(0, 4);
  return (
    <section className="bg-oc-sand border-b border-oc-hairline" data-testid="home-contributor-snippets">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="eyebrow text-oc-coral mb-4">Contributors</div>
            <h2 className="font-editorial text-4xl md:text-5xl leading-[1.05] tracking-tight text-oc-ink">
              Real operators. Real experience.
            </h2>
          </div>
          <Link to="/operators" className="font-ui text-xs font-bold uppercase tracking-widest text-oc-ink hover:text-oc-blue">
            All contributors &rarr;
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
          {picks.map((op) => (
            <Link
              key={op.slug}
              to={`/operators/${op.slug}`}
              data-testid={`contributor-snippet-${op.slug}`}
              className="group flex gap-5 items-start"
            >
              {op.portrait && (
                <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 overflow-hidden bg-oc-sand-warm border border-oc-hairline">
                  <img
                    src={op.portrait}
                    alt={op.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-center group-hover:scale-[1.04] transition-transform duration-500"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-ui font-bold text-lg text-oc-ink group-hover:text-oc-blue transition-colors">
                  {op.name}
                </div>
                <div className="font-ui text-sm text-oc-ink-muted mt-0.5">
                  {op.role}{op.company ? ` · ${op.company}` : ""}
                </div>
                {op.short_bio && (
                  <p className="mt-3 font-editorial text-base text-oc-ink-soft leading-snug line-clamp-3">
                    {op.short_bio}
                  </p>
                )}
                <div className="mt-3 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest text-oc-blue group-hover:text-oc-coral">
                  Read the interview <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* WHY IT EXISTS                                                       */
/* ------------------------------------------------------------------ */
export function WhyItExistsSection() {
  return (
    <section className="bg-oc-ink text-white" data-testid="home-why">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-8">
            <div className="eyebrow text-oc-coral mb-6">Why it exists</div>
            <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white">
              Most operating knowledge never gets written down.
            </h2>
            <p className="mt-8 font-editorial text-xl md:text-2xl text-white/75 leading-snug max-w-3xl">
              It lives in private conversations, hard experience, and decisions made under pressure. Operating Class is a slow, deliberate attempt to capture that knowledge and make it useful to the next operator who has to solve the same problem.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/about" className="inline-flex items-center h-12 px-6 border border-white/40 text-white font-ui text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-oc-ink transition-colors">
                About the publication
              </Link>
              <Link to="/manifesto" className="inline-flex items-center h-12 px-6 text-white font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-coral pb-0 hover:text-oc-coral">
                Read the manifesto <ArrowRight className="w-3.5 h-3.5 ml-2" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="border-l-2 border-oc-coral pl-6 py-2">
              <div className="eyebrow text-white/50 mb-3">Business model</div>
              <p className="font-editorial text-lg text-white/80 leading-relaxed">
                Operating Class is currently audience-first. There is nothing to buy today. Future revenue may come from sponsorship, partnerships, premium educational products, events, memberships, or research. Editorial independence stays separate from commercial relationships.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* NEWSLETTER INLINE                                                   */
/* ------------------------------------------------------------------ */
export function NewsletterInline() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/newsletter", { email, source: "home_inline" });
      setSent(true);
    } catch (err) {
      console.error("Newsletter subscribe failed:", err);
      setError("That email didn't go through. Please check the address and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-oc-sand-warm">
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="max-w-2xl mx-auto text-center">
          <div className="eyebrow text-oc-coral mb-4">The Newsletter</div>
          <h3 className="font-editorial text-3xl md:text-4xl leading-tight text-oc-ink">
            New interviews, operator lessons, and useful frameworks. Occasional, never noisy.
          </h3>
          {sent ? (
            <p data-testid="newsletter-success-home" className="mt-8 font-editorial text-lg text-oc-blue">
              You&#39;re on the list. Welcome to Operating Class.
            </p>
          ) : (
            <>
              <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                <input data-testid="newsletter-email-input-home" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="flex-1 h-14 px-5 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-base" />
                <button data-testid="newsletter-submit-home" disabled={loading} className="h-14 px-8 bg-oc-ink text-white font-ui text-sm font-bold uppercase tracking-widest hover:bg-oc-blue transition-colors disabled:opacity-60">
                  {loading ? "Sending..." : "Subscribe"}
                </button>
              </form>
              {error && (
                <p
                  data-testid="newsletter-error-home"
                  role="alert"
                  className="mt-4 font-ui text-sm text-oc-coral"
                >
                  {error}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
